import React, {useMemo, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery} from 'react-query';
import {GoBack} from '../components/GoBack';
import {
  DashboardLayout,
  FilterButton,
  PrimaryButton,
  TableLayout,
  PageLimit,
  Pagination,
  SearchInput,
  Loader,
  EmptyState,
  Filter,
  SkeletonLoader,
} from 'components';
import {
  baasBalance,
  baasMobile,
  bank,
  copy,
  corruptedFile,
  green,
  red,
} from 'assets/images/images';
import CardSummary from 'components/CardSummary';
import CardDetailsModal from './modals/CardDetailsModal';
import {
  fetchBAASCardDetailsSummary,
  fetchBAASCardInfo,
  fetchBAASCardTransactions,
} from 'requests/queries/baas';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {filterOutEmptyValues} from 'helpers';
import {handleCopy} from '..';
import {getStatusTextAndStyles} from 'helpers/getStatusTextAndStyles';
import {shortenLargeNumbers} from 'helpers/shortenLargeNumbers';
import AppliedFilters from 'components/AppliedFilters';

const initialFilters = {
  start: '',
  end: '',
};

function CustomerCardDetails() {
  const navigate = useNavigate();
  const {slug} = useParams();
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const [filters, setFilters] = useState(initialFilters);
  const [openFilter, setOpenFilter] = useState(false);
  const {userDetails} = useSelector(state => state?.auth);
  const [isCardDetailsOpen, setIsCardDetailsOpen] = useState(false);
  const [filterProps, setFilterProps] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const formatDate = date => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD');
  };

  const params = useMemo(
    () => ({
      card_id: slug,
    }),
    [slug],
  );

  const transactionsParams = useMemo(
    () => ({
      card_id: slug,
      environment: process.env.REACT_APP_ENVIRONMENT,
      start_date: formatDate(filters?.start),
      end_date: formatDate(filters?.end),
      search: searchText,
    }),
    [slug, filters?.start, filters?.end, searchText],
  );

  const summaryParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      card_id: slug,
      environment: process.env.REACT_APP_ENVIRONMENT,
      start_date: formatDate(filters?.start),
      search: searchText,
      end_date: formatDate(filters?.end),
    }),
    [userDetails, slug, filters?.start, filters?.end, searchText],
  );

  const {data: baas_card_info, isLoading: isLoadingInfo} = useQuery(
    ['baas-card-info', {params}],
    () => fetchBAASCardInfo({params}),
    {
      enabled: !!slug,
    },
  );

  const {data: baas_card_transactions, isLoading: isLoadingTransactions} =
    useQuery(
      ['baas-card-transactions', {transactionsParams}],
      () => fetchBAASCardTransactions({params: transactionsParams}),
      {
        enabled: !!slug,
      },
    );

  const {data: baas_card_summary, isLoading: isLoadingSummary} = useQuery(
    ['baas-card-summary', {summaryParams}],
    () => fetchBAASCardDetailsSummary({params: summaryParams}),
    {
      enabled: !!slug,
    },
  );

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
  };

  const handleResetFilter = () => {
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handleFilter = selectedOptions => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);
    setFilters(filteredOptions);
    setOpenFilter(false);
    setFiltersApplied(
      Object.values(filteredOptions).some(value => value) ||
        searchText.length > 0,
    );
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const isNotFiltered =
    !isLoadingTransactions &&
    (!baas_card_transactions?.entity?.transactions ||
      baas_card_transactions?.entity?.transactions.length === 0);
  const isFiltered =
    isNotFiltered &&
    (Object.values(filters).some(s => s) ||
      (searchText && searchText.length > 0));

  const DefaultEmptyState = () => {
    if (isFiltered) {
      return (
        <EmptyState
          height={45}
          noBtn
          customBtn={
            <button
              onClick={() => {
                setFilters(initialFilters);
                setSearchText('');
                setFiltersApplied(false);
                setOpenFilter(true);
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Update preferences
            </button>
          }
          heading="No Transactions Found"
          body="Oops, it looks like there are no transactions that match the filters/search term you've selected. Please try adjusting your filters."
        />
      );
    }

    return (
      <EmptyState
        height={45}
        noBtn
        className="w-full"
        src={corruptedFile}
        heading="No Transactions Found"
        body="Sorry, it appears there are no transactions available for this card."
        maxWidth="max-w-[500px]"
      />
    );
  };

  const cardDetailsFields = {
    fullName: `${baas_card_info?.entity?.billingDetails?.firstName || ''} ${
      baas_card_info?.entity?.billingDetails?.lastName || ''
    }`,
    cardType: baas_card_info?.entity?.cardType || 'N/A',
    cardNumber: `**** **** **** ${baas_card_info?.entity?.lastFour || 'N/A'}`,
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="" />
          <p className="ml-2 -mb-1">Customers</p>
        </div>
      }
    >
      <div className="flex items-center gap-4">
        <GoBack goBack={() => navigate(-1)} />
      </div>
      <div className="flex items-center justify-between mt-8 flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-grey text-[18px] font-medium">Card Details</h3>
          {filtersApplied && filterProps && (
            <AppliedFilters {...filterProps} isMainPage={true} />
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <SearchInput onChange={value => setSearchText(value)} />
          <FilterButton openFilter={openFilter} setOpenFilter={setOpenFilter} />
          <PrimaryButton
            buttonText="View Card Details"
            onClick={() => setIsCardDetailsOpen(true)}
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-7">
        <div className="flex flex-col gap-3">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2">
              <img alt="wallet" src={baasBalance} />

              <div className="flex flex-col gap-1">
                <p className="text-body font-medium text-sm">
                  Available Balance
                </p>
                {isLoadingInfo ? (
                  <SkeletonLoader
                    style={{
                      width: 150,
                      height: 25,
                      marginBottom: 10,
                    }}
                  />
                ) : (
                  <>
                    <h2 className="font-semibold text-[24px] text-grey">
                      ${baas_card_info?.entity?.availableBalance || '0'}
                    </h2>
                    {/* <h4 className="mt-1 text-[10px] font-medium text-deepBlue300">
                      Ledger Balance:{' '}
                      <span className="ml-2 font-semibold text-[14px]">
                        ${baas_card_info?.entity?.ledgerBalance || '0'}
                      </span>
                    </h4> */}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4 h-full">
            <CardSummary
              img={green}
              title="Total Top-up"
              body={`$${
                baas_card_summary?.entity?.collections?.toLocaleString() || '0'
              }`}
              titleClassName="text-sm"
              bodyClassName="text-[24px]"
              loading={isLoadingSummary}
            />
            <CardSummary
              img={red}
              title="Total Withdrawal"
              body={`$${
                baas_card_summary?.entity?.transfers?.toLocaleString() || '0'
              }`}
              titleClassName="text-sm"
              bodyClassName="text-[24px]"
              loading={isLoadingSummary}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center justify-between pb-3 border border-transparent border-b-[#E1E4EA]">
            <div className="flex items-center gap-2">
              <img src={bank} alt="bank" />
              <p className="font-medium text-black text-base">Card Details</p>
            </div>
            <button
              className="px-4 py-[6px] rounded-full border border-[#F2F4F7] text-sm text-black flex items-center gap-1  "
              onClick={() => {
                const allCardDetails = `Full Name: ${
                  cardDetailsFields.fullName
                }\nCard Type: ${cardDetailsFields.cardType}\nCard Number: ${
                  baas_card_info?.entity?.cardNumber || 'N/A'
                }`;
                handleCopy(allCardDetails);
              }}
            >
              <img src={copy} alt="copy" />
              Copy
            </button>
          </div>
          <div className="mt-4 flex flex-col gap-8">
            {Object.entries(cardDetailsFields).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between text-sm"
              >
                <p className="text-[#868C98] capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                {isLoadingInfo ? (
                  <SkeletonLoader
                    style={{
                      width: 150,
                      height: 25,
                      marginBottom: 10,
                    }}
                  />
                ) : (
                  <p className="text-black font-medium flex items-center   gap-2">
                    {value || 'N/A'}
                    {key === 'cardNumber' && (
                      <span
                        onClick={() =>
                          handleCopy(baas_card_info?.entity?.cardNumber)
                        }
                        className="cursor-pointer"
                      >
                        <img src={copy} alt="copy" />
                      </span>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CardDetailsModal
        show={isCardDetailsOpen}
        onClose={() => setIsCardDetailsOpen(false)}
        cardData={baas_card_info?.entity}
      />

      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={handleFilter}
        resetUrl={handleResetFilter}
        filterOptions={[]}
        onFilterStateChange={handleFilterStateChange}
      />

      <TableLayout negativeMargins className="w-full mt-4">
        {isLoadingTransactions ? (
          <Loader height={45} />
        ) : (
          <>
            <thead className="text-small font-semibold uppercase">
              <tr className="bg-white80">
                <td className="p-5 pl-12 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Transaction ID
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Transaction Amount
                </td>

                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Transaction Type
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Status
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Date/Time
                </td>
              </tr>
            </thead>
            <tbody className="text-[13px] text-body bg-white">
              {baas_card_transactions?.entity?.transactions?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center">
                    <DefaultEmptyState />
                  </td>
                </tr>
              ) : (
                baas_card_transactions?.entity?.transactions?.map(
                  transaction => (
                    <tr
                      key={transaction.transaction_id}
                      className="text-left border-b bg-white border-grey60"
                    >
                      <td className="p-4 pl-12 whitespace-nowrap">
                        {transaction.transaction_id}
                      </td>
                      <td className="p-4 pl-0 whitespace-nowrap">
                        {shortenLargeNumbers(
                          Number(transaction?.transaction_amount) || 0,
                          'USD',
                          {
                            startAt: 'M',
                          },
                        )}
                      </td>
                      <td className="p-4 pl-0 whitespace-nowrap">
                        {transaction.transaction_type || '-'}
                      </td>
                      <td className="p-4 pl-0 whitespace-nowrap">
                        <span
                          className={` ${
                            getStatusTextAndStyles(
                              transaction?.transaction_status?.toLowerCase(),
                            )?.styles
                          } uppercase w-fit rounded-full px-2 py-1 h-5   flex items-center justify-center  text-xs`}
                        >
                          {
                            getStatusTextAndStyles(
                              transaction?.transaction_status?.toLowerCase(),
                            )?.text
                          }
                        </span>
                      </td>
                      <td className="p-4 pl-0 whitespace-nowrap">
                        {moment(transaction.date_created).format(
                          'DD MMM YYYY h:mm A',
                        )}
                      </td>
                    </tr>
                  ),
                )
              )}
            </tbody>
          </>
        )}
      </TableLayout>

      {!isLoadingTransactions &&
        baas_card_transactions?.entity?.transactions?.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:gap-0">
            <PageLimit
              onLimitChange={length => {
                setQuery(prevState => ({
                  ...prevState,
                  length,
                  page: 1,
                }));
              }}
              limit={query?.length}
              total={baas_card_transactions?.entity?.total}
              length={baas_card_transactions?.entity?.transactions?.length}
            />

            <Pagination
              total={baas_card_transactions?.entity?.total_pages}
              current={Number(baas_card_transactions?.entity?.current_page)}
              onPageChange={activePage => {
                handlePageChange(activePage);
              }}
            />
          </div>
        )}
    </DashboardLayout>
  );
}

export default CustomerCardDetails;
